/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../app/hooks";
import { selectUser } from "../../userProfile/userSlice";
import InstallAppBanner from "../InstallAppBanner";
import CustomerJobsQueueRedesign from "./CustomerJobsQueueRedesign";
import * as api from "./apiService";

const statuses = [
  { id: "All", name: "All open jobs" },
  { id: "U", name: "Submitted" },
  { id: "A", name: "Received" },
  { id: "S", name: "Assigned" },
  { id: "W", name: "In progress" },
];

const allOption = { id: "All", name: "All" };

function readStoredObject(key, fallback) {
  try {
    const value = JSON.parse(localStorage.getItem(key));
    return value && value.id != null ? value : fallback;
  } catch {
    return fallback;
  }
}

function getDueFlags(job) {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  const dueToday = Boolean(job.completeBy?.includes(`${month}/${day}`));
  let overdue = false;

  if (job.completeByFullDate) {
    const completeBy = new Date(job.completeByFullDate);
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    overdue = !Number.isNaN(completeBy.getTime()) && completeBy < startOfToday;
  }

  return { dueToday, overdue };
}

const CustomerHome = () => {
  const currentUser = useAppSelector(selectUser);
  const [jobs, setJobs] = useState([]);
  const [totalJobs, setTotalJobs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [availableStatuses, setAvailableStatuses] = useState(statuses);
  const [customers, setCustomers] = useState([]);
  const [airports, setAirports] = useState([]);
  const [tags, setTags] = useState([]);
  const [searchText, setSearchText] = useState(localStorage.getItem("searchText") || "");
  const [statusSelected, setStatusSelected] = useState(() =>
    readStoredObject("statusSelected", statuses[0]),
  );
  const [customerSelected, setCustomerSelected] = useState(() =>
    readStoredObject("customerSelected", allOption),
  );
  const [airportSelected, setAirportSelected] = useState(() =>
    readStoredObject("airportSelected", allOption),
  );

  useEffect(() => {
    const nextStatuses = statuses.map((status) => (
      status.id === "A" && currentUser?.isCustomer
        ? { ...status, name: "Confirmed" }
        : status
    ));
    setAvailableStatuses(nextStatuses);
    setStatusSelected((selected) => (
      selected.id === "A" ? nextStatuses.find((status) => status.id === "A") : selected
    ));
  }, [currentUser?.isCustomer]);

  useEffect(() => {
    const loadFilters = async () => {
      const settled = await Promise.allSettled([
        api.getCustomers({ name: "", open_jobs: true }),
        api.getAirports({ open_jobs: true, onlyIncludeCustomerJobs: true }),
        api.getTags(),
      ]);
      const [customerResult, airportResult, tagResult] = settled;

      if (customerResult.status === "fulfilled") {
        setCustomers(customerResult.value.data.results || []);
      }
      if (airportResult.status === "fulfilled") {
        setAirports([allOption, ...(airportResult.value.data.results || [])]);
      }
      if (tagResult.status === "fulfilled") {
        setTags((tagResult.value.data.results || []).map((tag) => ({ ...tag, selected: false })));
      }
    };

    loadFilters();
  }, []);

  useEffect(() => localStorage.setItem("searchText", searchText), [searchText]);
  useEffect(() => localStorage.setItem("statusSelected", JSON.stringify(statusSelected)), [statusSelected]);
  useEffect(() => localStorage.setItem("customerSelected", JSON.stringify(customerSelected)), [customerSelected]);
  useEffect(() => localStorage.setItem("airportSelected", JSON.stringify(airportSelected)), [airportSelected]);

  const searchJobs = async () => {
    setLoading(true);
    const request = {
      searchText,
      status: statusSelected.id,
      sortField: "requestDate",
      airport: airportSelected.id,
      tags: tags.filter((tag) => tag.selected).map((tag) => tag.id),
      customer: customerSelected.id,
    };

    try {
      const { data } = await api.searchJobs(request);
      const normalizedJobs = (data.results || []).map((job) => {
        const dueFlags = getDueFlags(job);
        return { ...job, isDueToday: dueFlags.dueToday, isOverdue: dueFlags.overdue };
      });
      setJobs(normalizedJobs);
      setTotalJobs(data.count || 0);
    } catch {
      setJobs([]);
      setTotalJobs(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(searchJobs, 300);
    return () => clearTimeout(timeoutId);
  }, [searchText, statusSelected, airportSelected, tags, customerSelected]);

  const toggleTag = (tag) => {
    setTags((currentTags) => currentTags.map((item) => (
      item.id === tag.id ? { ...item, selected: !item.selected } : item
    )));
  };

  const clearAll = () => {
    setSearchText("");
    setStatusSelected(availableStatuses[0]);
    setCustomerSelected(allOption);
    setAirportSelected(allOption);
    setTags((currentTags) => currentTags.map((tag) => ({ ...tag, selected: false })));
  };

  return (
    <>
      <InstallAppBanner
        appStoreUrl="https://apps.apple.com/app/id6748924512"
        appSchemeUrl="livetakeoff://jobs"
      />
      <CustomerJobsQueueRedesign
        currentUser={currentUser}
        jobs={jobs}
        loading={loading}
        totalJobs={totalJobs}
        searchText={searchText}
        onSearchChange={setSearchText}
        onSearchKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            searchJobs();
          }
        }}
        onRefresh={searchJobs}
        filtersOpen={filtersOpen}
        onFiltersOpen={() => setFiltersOpen(true)}
        onFiltersClose={() => setFiltersOpen(false)}
        statuses={availableStatuses}
        statusSelected={statusSelected}
        onSelectStatus={setStatusSelected}
        customers={customers}
        customerSelected={customerSelected}
        onSelectCustomer={setCustomerSelected}
        airports={airports}
        airportSelected={airportSelected}
        onSelectAirport={setAirportSelected}
        tags={tags}
        onToggleTag={toggleTag}
        onClearAll={clearAll}
      />
    </>
  );
};

export default CustomerHome;
